import type { FixtureExport } from '../core/fixtureApi.js';
import type { FixtureNode } from '../core/FixtureNode.js';
import type { ExplorerRuntime, FixtureModules } from '../core/index.js';
import { createFixtureTree } from '../core/FixtureNode.js';
import { ComponentRendering } from '../core/ComponentRenderer.js';

export class EmbeddedMode implements ExplorerRuntime {
	private _root: FixtureNode | undefined;
	private _rendering: ComponentRendering | undefined;
	private _resizeObserver: ResizeObserver | undefined;

	constructor(
		private readonly _container: HTMLElement,
		private readonly _fixtureId: string,
		loadSourceModule: Promise<FixtureModules>,
	) {
		loadSourceModule.then(modules => {
				this._root = this._buildTree(modules);
				this._render();
		});
	}

	updateFixtures(fixtureModules: FixtureModules): void {
		this._root = this._buildTree(fixtureModules);
		this._render();
	}

	bumpVersion(): void {
		this._render();
	}

	private _render(): void {
		this._rendering?.dispose();
		this._rendering = undefined;
		this._resizeObserver?.disconnect();
		this._resizeObserver = undefined;
		this._container.innerHTML = '';

		if (!this._root) return;

		const node = this._findNode(this._root, this._fixtureId);
		if (!node?.component) {
			console.error(`[EmbeddedMode] Fixture not found: ${this._fixtureId}`);
			return;
		}

		const component = { ...node.component, isolation: 'none' as const };
		const rendering = new ComponentRendering(this._container, component);
		this._rendering = rendering;

		const fixtureId = this._fixtureId;
		const postSize = () => {
			const rect = rendering.renderTarget.getBoundingClientRect();
			window.parent.postMessage({
				type: 'component-explorer:resize',
				fixtureId,
				width: rect.width,
				height: rect.height,
			}, '*');
		};

		this._resizeObserver = new ResizeObserver(postSize);
		this._resizeObserver.observe(rendering.renderTarget);
	}

	private _buildTree(fixtureModules: FixtureModules): FixtureNode {
		const fixtures = new Map<string, FixtureExport>();
		for (const [path, module] of Object.entries(fixtureModules)) {
			const fixture = module.default;
			if (fixture && typeof fixture === 'object') {
				fixtures.set(path, fixture as FixtureExport);
			}
		}
		return createFixtureTree(fixtures);
	}

	private _findNode(node: FixtureNode, id: string): FixtureNode | undefined {
		if (node.id.value === id) return node;
		for (const child of node.children ?? []) {
			const found = this._findNode(child, id);
			if (found) return found;
		}
		return undefined;
	}
}
